// Copyright 2018 Sorama B.V.

import { computedFrom } from "aurelia-binding";
import { Corners } from "controls/plot/scan-planner/ScanPlanExtensions";
import { RecordingStep, ScanArea } from "datatypes/ScanStep";
import { Point2D } from "datatypes/SoramaLocalApi";

export class OrderedRecordingStepGroup {
    public positionInArrays: Point2D;
    public steps: OrderedRecordingSteps;

    public get scanArea(): ScanArea {
        return this.steps.scanArea;
    }

    public get isCompleted(): boolean {
        return this.recordingSteps.findIndex((x) => !x.recordingId) === -1;
    }

    @computedFrom("steps", "steps.steps")
    public get recordingSteps(): RecordingStep[] {
        return this.steps.steps;
    }

    public get numberOfCompletedRecordingSteps(): number {
        return this.steps.steps.filter((x) => x.recordingId).length;
    }

    constructor(steps: OrderedRecordingSteps, positionInArrays: Point2D) {
        this.positionInArrays = positionInArrays;
        this.steps = steps;
    }

    public static sort(
        steps: OrderedRecordingStepGroup[],
        startCorner: Corners = Corners.TopLeft,
        stride: "x" | "y" = "x"
    ): OrderedRecordingStepGroup[] {
        const sortedSteps: OrderedRecordingStepGroup[] = [];

        // Find start origin
        let origin: Point2D = { x: 0, y: 0 };
        switch (startCorner) {
            case Corners.BottomRight:
                origin.y += Math.max(...steps.map((x) => x.positionInArrays.y));
                break;
            case Corners.TopRight:
                origin.x += Math.max(...steps.map((x) => x.positionInArrays.x));
                break;
            case Corners.BottomLeft:
                origin.y += Math.max(...steps.map((x) => x.positionInArrays.y));
                break;
            default:
                break;
        }

        // Find starting edge, closest to origin
        const horizontalWeight = stride === "x" ? 1 : 1 + Math.max(...steps.map((x) => x.positionInArrays.y)); // Make sure that weight is always higher or equal to 1
        const verticalWeight = stride === "y" ? 1 : 1 + Math.max(...steps.map((x) => x.positionInArrays.x));

        while (steps.length > 0) {
            const idx = OrderedRecordingStepGroup.findIndexNextStep(steps, origin, horizontalWeight, verticalWeight);
            const step = steps.splice(idx, 1)[0];
            origin = { x: step.positionInArrays.x, y: step.positionInArrays.y };
            sortedSteps.push(step);
        }

        // also sort recording steps
        sortedSteps.forEach((x) => x.steps.sortSteps(startCorner, stride));

        return sortedSteps;
    }

    private static findIndexNextStep(
        steps: OrderedRecordingStepGroup[],
        currentPosition: Point2D,
        horizontalWeight: number,
        verticalWeight: number
    ): number {
        let distance = Number.MAX_VALUE;
        let idx = -1;

        for (let i = 0; i < steps.length; i++) {
            const x = horizontalWeight * (currentPosition.x - steps[i].positionInArrays.x);
            const y = verticalWeight * (currentPosition.y - steps[i].positionInArrays.y);
            const calcDistance = Math.sqrt(x * x + y * y);

            if (calcDistance < distance) {
                distance = calcDistance;
                idx = i;
            }
        }

        return idx;
    }
}

export class OrderedRecordingSteps {
    private _steps: RecordingStep[];
    private _scanArea: ScanArea;

    @computedFrom("_steps")
    public get steps(): RecordingStep[] {
        return this._steps;
    }

    @computedFrom("_scanArea")
    public get scanArea(): ScanArea {
        return this._scanArea;
    }

    private currentStride: "x" | "y";
    private currentStartCorner: Corners;

    constructor(
        steps: RecordingStep[],
        scanArea?: ScanArea,
        corner: Corners = Corners.TopLeft,
        stride: "x" | "y" = "x"
    ) {
        this._scanArea = scanArea;
        this._steps = steps;
        this.currentStride = stride;
        this.currentStartCorner = corner;

        if (!this._scanArea) {
            const sensorPositions = steps.map((x) => x.position);

            const width = Math.max(...sensorPositions.map((x) => x.X)) - Math.min(...sensorPositions.map((x) => x.X));
            const height = Math.max(...sensorPositions.map((x) => x.Y)) - Math.min(...sensorPositions.map((x) => x.Y));

            this._scanArea = {
                height: height,
                width: width,
                offsetX: 0,
                offsetY: 0,
                offsetZ: 0,
            };
        }

        this.sortSteps(this.currentStartCorner, this.currentStride);
    }

    public splitStepsInGlobalSteps(
        measurementDeviceDimensions: Point2D,
        startCorner?: Corners,
        direction?: "x" | "y"
    ): OrderedRecordingStepGroup[] {
        const globalDimensionsInSteps: Point2D = {
            x: Math.max(1, Math.round(this._scanArea.width / measurementDeviceDimensions.x)),
            y: Math.max(1, Math.round(this._scanArea.height / measurementDeviceDimensions.y)),
        };

        startCorner = startCorner ? startCorner : this.currentStartCorner;
        direction = direction ? direction : this.currentStride;

        return this.generateBasicStepsFromProperties(globalDimensionsInSteps, startCorner, direction).map((x) => {
            const area: ScanArea = {
                offsetX: x.x * measurementDeviceDimensions.x,
                offsetY: x.y * measurementDeviceDimensions.y,
                width: measurementDeviceDimensions.x,
                height: measurementDeviceDimensions.y,
                offsetZ: 0,
            };

            const steps = new OrderedRecordingSteps(this.stepsInArea(area), area, startCorner, direction);

            return new OrderedRecordingStepGroup(steps, { x: x.x, y: x.y });
        });
    }

    public stepsInArea(area: ScanArea): RecordingStep[] {
        return this._steps.filter((x) => {
            return (
                x.position.X >= area.offsetX - area.width / 2 &&
                x.position.X < area.offsetX + area.width / 2 &&
                x.position.Y > area.offsetY - area.height / 2 &&
                x.position.Y <= area.offsetY + area.height / 2
            );
        });
    }

    public sortSteps(startCorner: Corners, direction: "x" | "y"): void {
        this.currentStartCorner = startCorner;
        this.currentStride = direction;

        const sortedSteps: RecordingStep[] = [];

        // Find start origin. Y is up.
        let origin: Point2D = { x: this._scanArea.offsetX, y: this._scanArea.offsetY };
        switch (startCorner) {
            case Corners.TopRight:
                origin.y += this._scanArea.height;
                break;
            case Corners.BottomRight:
                origin.x += this._scanArea.width;
                break;
            case Corners.TopLeft:
                origin.y += this._scanArea.height;
                break;
            default:
                break;
        }

        // Find starting edge, closest to origin
        const horizontalWeight = direction === "x" ? 1 : 1 + this._scanArea.height; // Make sure that weight is always higher or equal to 1
        const verticalWeight = direction === "y" ? 1 : 1 + this._scanArea.width;

        while (this._steps.length > 0) {
            const idx = this.findIndexNextStep(this._steps, origin, horizontalWeight, verticalWeight);
            const step = this._steps.splice(idx, 1)[0];
            origin = { x: step.position.X, y: step.position.Y };
            sortedSteps.push(step);
        }

        sortedSteps.forEach((x, i) => (x.stepIndex = i));

        this._steps.push(...sortedSteps);
    }

    private findIndexNextStep(
        steps: RecordingStep[],
        currentPosition: Point2D,
        horizontalWeight: number,
        verticalWeight: number
    ): number {
        let distance = Number.MAX_VALUE;
        let idx = -1;

        for (let i = 0; i < steps.length; i++) {
            const x = horizontalWeight * (currentPosition.x - steps[i].position.X);
            const y = verticalWeight * (currentPosition.y - steps[i].position.Y);
            const calcDistance = Math.sqrt(x * x + y * y);

            if (calcDistance < distance) {
                distance = calcDistance;
                idx = i;
            }
        }

        return idx;
    }

    private generateBasicStepsFromProperties(
        dimensions: Point2D,
        startCorner: Corners,
        direction: "x" | "y"
    ): Point2D[] {
        const newSteps: Point2D[] = [];

        const xMax = dimensions.x - 1;
        const yMax = dimensions.y - 1;

        let x = 0;
        let y = 0;

        switch (startCorner) {
            case Corners.TopRight:
                x = xMax;
                y = yMax;
                break;
            case Corners.BottomLeft:
                x = 0;
                y = 0;
                break;
            case Corners.BottomRight:
                x = xMax;
                y = 0;
                break;
            case Corners.None: // Just use topleft as default
            case Corners.TopLeft:
            default:
                x = 0;
                y = yMax;
                break;
        }

        let xDir = x > 0 ? -1 : 1;
        let yDir = y > 0 ? -1 : 1;

        let xLimit = xDir < 0 ? 0 : xMax;
        let yLimit = yDir < 0 ? 0 : yMax;

        for (let idx = 0; idx < dimensions.x * dimensions.y; idx++) {
            newSteps.push(<Point2D>{ x: x, y: y });

            if (direction == "x") {
                if (x == xLimit) {
                    xDir *= -1;
                    y += yDir;
                    xLimit += xDir * xMax;
                } else {
                    x += xDir;
                }
            } else {
                if (y == yLimit) {
                    yDir *= -1;
                    x += xDir;
                    yLimit += yDir * yMax;
                } else {
                    y += yDir;
                }
            }
        }

        return newSteps;
    }
}
